import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { devError } from '@/shared/util/devLog';
import { auth } from '@/shared/services/firebase';
import { useAuth } from '@/shared/contexts/AuthContext';
import { verifyUser } from '../services';
import { loginSchema, LoginFormData } from '../types';

export function useLogin() {
	const { completeLogin } = useAuth();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const navigate = useNavigate();

	const { control, handleSubmit } = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
		defaultValues: { email: '', password: '' },
		mode: 'onChange',
	});

	const onSubmit = async (data: LoginFormData) => {
		setLoading(true);
		setError('');

		try {
			const userCredentials = await signInWithEmailAndPassword(auth, data.email, data.password);
			await completeLogin(userCredentials.user);
			navigate('/');
		} catch {
			setError('Error al iniciar sesión. Verifica tus credenciales.');
		}
		setLoading(false);
	};

	const handleGoogleLogin = async () => {
		setError('');
		setLoading(true);
		try {
			const provider = new GoogleAuthProvider();
			const googleResult = await signInWithPopup(auth, provider);

			const isValid = await verifyUser(googleResult.user);
			if (isValid) {
				await completeLogin(googleResult.user);
				navigate('/');
				return;
			}
		} catch (error) {
			devError(error);
		}
		setError('Error al iniciar sesión. Verifica tus credenciales.');
		setLoading(false);
	};

	return {
		control,
		loading,
		error,
		onSubmit: handleSubmit(onSubmit),
		handleGoogleLogin,
	};
}
